import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { FormGroup, Label } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { differenceWith, debounce, get } from 'lodash';

import { ItemList, Spinner } from '../../components';
import { getOnboardingDocList, getTaskListDocs, getCompany, GET_ONBOARDING_DOC_LIST, GET_TASKLIST_DOCS, LOADING_PROP_TYPE } from '../../modules';
import { DROPDOWN_SEARCH_DEBOUNCE_TIME } from '../../constants';
import { esignProductTierNames } from '../../constants/esignProductTierNames';


class TaskList extends PureComponent {
  static formatSelectOptions(docs) {
    return docs.map(doc => ({ label: doc.title ? `${doc.title} - ${doc.filename}` : doc.filename, value: doc.esignID || doc.id }));
  }

  constructor(props) {
    super(props);
    this.state = {
      target: document.getElementById('tasklist-ui'),
      selectOptions: [],
      itemListOptions: [],
      selectedCompanyId: get(window, 'asure.appSettings.selectedCompany.companyId'),
      initialLoad: true,
    };
  }

  componentDidMount() {
    const { target, selectedCompanyId } = this.state;
    if (target) {
      this.props.getOnboardingDocList();
      this.props.getTaskListDocs();
      if (selectedCompanyId) {
        this.props.getCompany(selectedCompanyId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { docs: { onboardingDocs: { results: esignatureDocs }, taskListDocs }, selectedCompany, loading } = this.props;
    const { docs: { onboardingDocs: { results: prevEsignDocs }, taskListDocs: prevTaskListDocs } } = prevProps;
    const companyIsLegacy = selectedCompany && selectedCompany.isEsignatureLegacyCompany;
    const companyIsSimpleTier = selectedCompany && selectedCompany.esignatureProductTier.name === esignProductTierNames.SIMPLESIGN;
    const companyIsSimpleSign = !companyIsLegacy && companyIsSimpleTier;
    if (esignatureDocs && esignatureDocs.length !== prevEsignDocs.length) {
      const uniqueEsignDocs = differenceWith(esignatureDocs, taskListDocs, this.compareObDocToDoc);
      const filteredEsignDocs = companyIsSimpleSign ? uniqueEsignDocs.filter(doc => !doc.isHelloSignTemplate) : uniqueEsignDocs;
      this.addDataToState('selectOptions', filteredEsignDocs);
    }
    if (taskListDocs && taskListDocs.length !== prevTaskListDocs.length) {
      this.addDataToState('itemListOptions', taskListDocs);
    }
    if (this.state.initialLoad) {
      this.addDataToState('initialLoad', !(loading[GET_ONBOARDING_DOC_LIST] === 'complete' && loading[GET_TASKLIST_DOCS] === 'complete'));
    }
  }

  searchDocs(search) {
    const searchNext = this.props.docs.onboardingDocs.queryNext[search];
    if (searchNext) {
      this.props.getOnboardingDocList(searchNext);
    } else {
      this.props.getOnboardingDocList('', search);
    }
  }
  debounceSearchDocs = debounce(this.searchDocs, DROPDOWN_SEARCH_DEBOUNCE_TIME);

  addDataToState(property, data) {
    this.setState({ [property]: data });
  }

  convertEsignID(esignID) {
    const { docs: { onboardingDocs: { results: esignatureDocs } } } = this.props;
    return esignatureDocs.filter(doc => doc.esignID === esignID).map(doc => doc.id)[0];
  }

  // checks ID and EsignId of OB doc against ID of doc
  compareObDocToDoc = (obDoc, doc) => (obDoc.esignID ? obDoc.esignID === doc.id : obDoc.id === doc.id)

  updateSelectOptions(docIdVal, operation) {
    const { selectOptions, itemListOptions } = this.state;
    const { docs: { onboardingDocs: { results: esignatureDocs } } } = this.props;
    const docId = this.convertEsignID(docIdVal) || docIdVal;
    if (operation === 'remove') {
      // Remove the document from the select and add it to the item list
      this.setState({
        selectOptions: selectOptions.filter(doc => doc.id !== docId),
        itemListOptions: [...itemListOptions, ...esignatureDocs.filter(doc => doc.id === docId)],
      });
      // Trigger an event to notify ADHR 2.0 that we've added a doc to the item list
      const docAdded = new CustomEvent('taskListDocAdded', { detail: { docId: docIdVal } });
      document.dispatchEvent(docAdded);
    } else if (operation === 'add') {
      // Remove the document from the item list and add it to back into the select
      const isFetchedDoc = !itemListOptions.filter(doc => doc.id === docId)[0];
      this.setState({
        selectOptions: [...selectOptions, ...esignatureDocs.filter(doc => doc.id === docId)],
        itemListOptions: isFetchedDoc ? itemListOptions.filter(doc => doc.id !== docIdVal) : itemListOptions.filter(doc => doc.id !== docId),
      });
      // Trigger an event to notify ADHR 2.0 that we've removed a doc from the item list
      const docRemoved = new CustomEvent('taskListDocRemoved', { detail: { docId: docIdVal } });
      document.dispatchEvent(docRemoved);
    }
  }

  removeDocFromTaskList(docId) {
    this.updateSelectOptions(docId, 'add');
  }

  fetchOnboardingDocs() {
    const { docs: { onboardingDocs: { next } } } = this.props;
    if (next) {
      this.props.getOnboardingDocList(next);
    }
  }

  render() {
    const { loading } = this.props;
    const {
      target,
      selectOptions,
      itemListOptions,
      initialLoad,
    } = this.state;
    const isLoading = loading[GET_ONBOARDING_DOC_LIST] === 'loading';
    const componentContent = (
      <Fragment>
        <FormGroup>
          <Label for="companyDocs"><FormattedMessage id="SELECT_COMPANY_DOCUMENTS" /></Label>
          <Select
            name="companyDocs"
            options={TaskList.formatSelectOptions(selectOptions)}
            classNamePrefix="react-select-container"
            onChange={option => this.updateSelectOptions(option.value, 'remove')}
            isLoading={isLoading}
            placeholder="Select a document"
            onMenuScrollToBottom={() => this.fetchOnboardingDocs()}
            onInputChange={(currentInput) => { this.debounceSearchDocs.bind(this, currentInput)(); }}
            components={{ LoadingIndicator: Spinner }}
          />
        </FormGroup>
        <ItemList
          items={itemListOptions}
          onClear={this.removeDocFromTaskList.bind(this)}
          title="DOCUMENTS"
          isLoading={initialLoad}
        />
      </Fragment>
    );
    if (target) {
      return createPortal(componentContent, target);
    }
    return null;
  }
}

TaskList.propTypes = {
  docs: PropTypes.shape({
    esignDocs: PropTypes.shape({
      next: PropTypes.string,
      results: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        filename: PropTypes.string,
        title: PropTypes.string,
        uploadDate: PropTypes.string,
        uploadedBy: PropTypes.string,
        category: PropTypes.string,
        isEsignatureDocument: PropTypes.bool,
        isHelloSignTemplate: PropTypes.bool,
      })),
    }),
    onboardingDocs: PropTypes.shape({
      next: PropTypes.string,
      queryNext: PropTypes.object,
      results: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        filename: PropTypes.string,
        title: PropTypes.string,
        uploadDate: PropTypes.string,
        uploadedBy: PropTypes.string,
        category: PropTypes.string,
        isEsignatureDocument: PropTypes.bool,
        isHelloSignTemplate: PropTypes.bool,
        isPublishedToEmployee: PropTypes.bool,
        isLegacyDocument: PropTypes.bool,
        IsOnboardingDocument: PropTypes.bool,
      })),
    }),
    taskListDocs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string,
    })),
  }).isRequired,
  selectedCompany: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    esignatureProductTier: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    isEsignatureLegacyCompany: PropTypes.bool,
  }).isRequired,
  getOnboardingDocList: PropTypes.func.isRequired,
  getTaskListDocs: PropTypes.func.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  getCompany: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    loading: state.appState.loading,
    docs: state.resources.docs,
    selectedCompany: state.resources.companies.selectedCompany,
  }),
  {
    getOnboardingDocList: (next, search) => getOnboardingDocList(next, search),
    getTaskListDocs: () => getTaskListDocs(),
    getCompany,
  }
)(TaskList);



// WEBPACK FOOTER //
// ./src/containers/TaskList/TaskList.jsx